import { motion } from "framer-motion";
import { Music } from "lucide-react";
import React from "react";
import { PlayerProgress } from "./PlayerProgress";
import { PlayerTrackInfo } from "./PlayerTrackInfo";

interface MiniPlayerProps {
  title: string;
  artist: string;
  albumArt?: string;
  isPlaying: boolean;
  progress: number;
  duration: number;
  isDragging: boolean;
  dragProgress: number | null;
  isLiked: boolean;
  isHapticsSupported: boolean;
  isHapticsEnabled: boolean;
  onPlayPause: () => void;
  onExpand: () => void;
  onWindowedMode: () => void;
  onSeekStart: (e: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>) => void;
  onSeek: (e: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>) => void;
  onSeekEnd: (e: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>) => void;
  onLikeToggle: () => void;
  onShare: () => void;
  onHapticsToggle: () => void;
  onOpenMenu: () => void;
  onHapticPresetOpen: () => void;
  trackId: string;
}

export const MiniPlayer: React.FC<MiniPlayerProps> = ({
  title,
  artist,
  albumArt,
  isPlaying,
  progress,
  duration,
  isDragging,
  dragProgress,
  isLiked,
  isHapticsSupported,
  isHapticsEnabled,
  onPlayPause,
  onExpand,
  onWindowedMode,
  onSeekStart,
  onSeek,
  onSeekEnd,
  onLikeToggle,
  onShare,
  onHapticsToggle,
  onOpenMenu,
  onHapticPresetOpen,
  trackId,
}) => {
  return (
    <div className="relative w-full">
      <PlayerProgress
        progress={progress}
        duration={duration}
        isDragging={isDragging}
        dragProgress={dragProgress}
        onSeekStart={onSeekStart}
        onSeek={onSeek}
        onSeekEnd={onSeekEnd}
        mini
      />

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 20 }}
        className="max-w-screen-xl mx-auto flex items-center justify-between gap-4 p-4"
      >
        <motion.div layout className="flex items-center gap-3 flex-1 min-w-0">
          <motion.div
            layoutId="albumArt"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={onExpand}
            className="cursor-pointer min-w-12 h-12"
          >
            {albumArt ? (
              <img src={albumArt} alt={title} className="w-12 h-12 rounded-md object-cover" />
            ) : (
              <div className="w-12 h-12 rounded-md bg-zinc-800 flex items-center justify-center">
                <Music className="w-6 h-6 text-zinc-400" />
              </div>
            )}
          </motion.div>
          <motion.div layout="position" className="max-w-[42svw] md:min-w-fit flex-grow">
            <a href={`/track/${trackId}`}>
              <motion.h4 layoutId="title" className="font-medium truncate hover:underline">
                {title}
              </motion.h4>
            </a>
            <motion.p layoutId="artist" className="text-sm text-zinc-400 truncate">
              {artist}
            </motion.p>
          </motion.div>
          <PlayerTrackInfo
            title={title}
            artist={artist}
            albumArt={albumArt}
            isPlaying={isPlaying}
            isLiked={isLiked}
            isHapticsSupported={isHapticsSupported}
            isHapticsEnabled={isHapticsEnabled}
            onLikeToggle={onLikeToggle}
            onHapticsToggle={onHapticsToggle}
            onPlayPause={onPlayPause}
            onExpand={onExpand}
            onWindowedMode={onWindowedMode}
            onOpenMenu={onOpenMenu}
            onHapticPresetOpen={onHapticPresetOpen}
            mini
          />
        </motion.div>
      </motion.div>
    </div>
  );
};
